import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import preferences from 'Preferences';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormInput from 'ui-library/bs/FormInput';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import * as keyboard from 'util/Keyboard';
import {ElementKeyboardEvent, InputElementFocusEvent} from 'util/Types';

import FieldActionButtons from './FieldActionButtons';

export interface BaseAliasFieldProps {
  id?: string;
  isFullWidth: boolean;
  isWritable: boolean;
}

export interface AliasFieldProps extends BaseAliasFieldProps {
  archive?: (alias: string) => void;
  allowEmpty: boolean;
  className?: string;
  cancel?: (index?: number) => void;
  complete?: (newAlias: string, index?: number) => Promise<string | void> | void;
  edit?: () => void;
  initialAlias: string;
  initialIsBeingEdited: boolean;
  /** When true, the field expands to full width only while being actively edited. */
  isFullWidthWhenEditing?: boolean;
  isHeader: boolean;
  placeholder?: string;
  remove?: (alias: string) => void;
  subtitle?: string;
  unarchive?: (alias: string) => void;
  validate?: (initialValue: string, newValue: string) => Promise<unknown> | boolean;
}

interface State {
  isBeingEdited: boolean;
  alias: string;
}

class AliasField extends React.Component<AliasFieldProps, State> {
  static defaultProps = {
    allowEmpty: false,
    initialAlias: '',
    initialIsBeingEdited: false,
    isFullWidth: false,
    isHeader: false,
    placeholder: 'Alias',
  };

  getInitialState(): State {
    return {
      isBeingEdited: this.props.initialIsBeingEdited,
      alias: this.props.initialAlias,
    };
  }

  constructor(props: AliasFieldProps) {
    super(props);
    this.state = this.getInitialState();
  }

  override UNSAFE_componentWillReceiveProps(nextProps: AliasFieldProps): void {
    if (!this.state.isBeingEdited) {
      // If the field is currently being edited, re-rendering would cause the user to lose their work.
      this.setState({alias: nextProps.initialAlias});
    }
  }

  private _isMounted = false;

  override componentDidMount(): void {
    this._isMounted = true;
  }

  override componentWillUnmount(): void {
    this._isMounted = false;
  }

  private handleKeyDown = async (e: ElementKeyboardEvent): Promise<void> => {
    if (keyboard.isEnterKey(e)) {
      e.preventDefault();
      if (this.state.alias) {
        await this.complete();
      }
    } else if (keyboard.isEscapeKey(e)) {
      e.preventDefault();
      this.cancel();
    }
  };

  /**
   * Can be called by the parent to reset our internal state in the case that alias update is rolled back
   */
  reset = (): void => {
    this.setState(this.getInitialState());
  };

  private cancel = (): void => {
    if (typeof this.props.cancel === 'function') {
      this.props.cancel();
    }
    this.reset();
  };

  private complete = async (): Promise<void> => {
    if (this.props.validate != null) {
      const isValid = await this.props.validate(this.props.initialAlias, this.state.alias);
      if (!isValid) {
        return;
      }
    }
    this.setState({isBeingEdited: false});
    if (this.props.complete) {
      const alias = await this.props.complete(this.state.alias);
      if (alias != null) {
        this.setState({alias});
      } else if (this._isMounted) {
        this.reset();
      }
    }
  };

  private archive = (): void => {
    assert(this.props.archive != null, 'Expected non-null archive prop');
    this.setState({isBeingEdited: false});
    this.props.archive(this.state.alias);
  };

  private copyId = (): void => {
    assert(this.props.id != null, 'Expected non-null id prop');
    copyToClipboard(this.props.id, {showToastOnSuccess: true});
  };

  private unarchive = (): void => {
    assert(this.props.unarchive != null, 'Expected non-null remove prop');
    this.setState({isBeingEdited: false});
    this.props.unarchive(this.state.alias);
  };

  private remove = (): void => {
    assert(this.props.remove != null, 'Expected non-null unarchive prop');
    this.setState({isBeingEdited: false});
    this.props.remove(this.state.alias);
  };

  private edit = (): void => {
    this.setState({isBeingEdited: true});
    if (this.props.edit != null) {
      this.props.edit();
    }
  };

  override render = (): JSX.Element => {
    const isFullWidth =
      this.props.isFullWidth || (Boolean(this.props.isFullWidthWhenEditing) && this.state.isBeingEdited);
    return (
      <div
        {...getTestProps({component: 'AliasField', element: 'root'})}
        className={classNames('metadata-field', this.props.className, {
          'is-beingEdited': this.state.isBeingEdited,
          'metadata-field--header': this.props.isHeader,
          'u-width-full': isFullWidth,
        })}
      >
        <div className={classNames('metadata-field-row', {'u-width-full': isFullWidth})}>
          <div
            className={classNames({
              'metadata-field-col': !this.props.isHeader,
              'u-width-full': isFullWidth,
            })}
          >
            {this.state.isBeingEdited ? (
              <FormInput
                autoFocus={true}
                placeholder={this.props.placeholder}
                value={this.state.alias}
                onChange={(e) => {
                  this.setState({alias: e.target.value});
                }}
                onKeyDown={this.handleKeyDown}
                onFocus={(e: InputElementFocusEvent) => {
                  const input = e.target;
                  input.setSelectionRange(input.value.length, input.value.length);
                }}
                {...getTestProps({component: 'AliasField', element: 'formInput'})}
              />
            ) : this.state.alias ? (
              <div
                {...getTestProps({component: 'AliasField', element: 'value'})}
                className={classNames({
                  'u-cursor-text': !this.state.isBeingEdited,
                  'u-overflow-wrap-break-word': isFullWidth,
                  'u-width-full': isFullWidth,
                })}
              >
                {this.state.alias}
              </div>
            ) : (
              ''
            )}
            {this.props.subtitle != null && (
              <div className="metadata-registry-header">{this.props.subtitle}</div>
            )}
          </div>
          <div
            className={classNames('registry-actionGroupContainer', {
              'registry-actionGroupContainer--header': this.props.isHeader,
            })}
          >
            {this.props.id && !this.state.isBeingEdited && Boolean(preferences.get('ENABLE_COPY_API_ID')) && (
              <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy API ID">
                <a
                  className="registry-actionGroup metadata-action faded u-primary-pointer"
                  onClick={this.copyId}
                >
                  <BenchlingIcon icon="note-code" />
                </a>
              </Tooltip>
            )}
            {this.props.isWritable && (
              <FieldActionButtons
                archive={this.props.archive ? this.archive : undefined}
                isEditing={this.state.isBeingEdited}
                cancel={this.cancel}
                complete={this.complete}
                isCompleteEnabled={this.props.allowEmpty || Boolean(this.state.alias)}
                remove={this.props.remove ? this.remove : undefined}
                edit={this.props.edit || this.props.complete ? this.edit : undefined}
                unarchive={this.props.unarchive ? this.unarchive : undefined}
              />
            )}
          </div>
        </div>
      </div>
    );
  };
}

export default AliasField;
